import { push } from 'connected-react-router'
import { all, call, put, take, takeEvery, select } from 'redux-saga/effects'
import { t } from 'ttag'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { ROUTES } from '../../../common/Routes/routePaths'
import api from '../../../services/api.services'
import { DeleteGoal$Request, GoalId, UserCompanyDto, UserProfileDto } from '../../../services/api.types'
import {
  createGoal,
  createGoalFail,
  createGoalSuccess,
  updateGoal,
  updateGoalFail,
  updateGoalSuccess,
  deleteGoal,
} from './ChallengeAdminActions'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../../../common/auth/authActions'
import { convertUTCToLocalDate } from '../../../common/helpers/date'
import { getCurrentCompanyGoal, setActivityListTab, setActivityListTypeTab } from '../../Activity/store/ActivityActions'
import { managedCompaniesSelector, userDtoSelector } from '../../Settings/store/SettingsSelectors'
import { getErrorMsg } from '../../../common/helpers/translations'
import { Action } from 'redux-actions'

function* sagaCreateGoal({ payload }: any) {
  const {
    //companyId,
    newCommunityName,
    divisionId,
    endDate,
    progressType,
    startDate,
    voucher,
    dailyStepsGoal,
    activityFormulaTarget,
    collectiveGoal,
    title,
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    chatEnabled,
    dailyPersistenceGoal,
    persistenceGoal,
    customType,
    customName,
    customUnit,
    customGoal,
    description,
    manualEntryEnabled,
    pointsLogic,
    totalPointsLogic,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,
    simpleManualEntry,
    includeInTotalPointsChallenges,
    dailyMax,
    leaderboards,
    goals,
    avgAgg,
    showGroupMembers,
    showGroupMembersContributions,
    hideLeaderboardTables,
  } = payload
  let companyId = payload.companyId
  let signUpCode = ''
  if (companyId === 'new') {
    try {
      // @ts-ignore
      const createUserOwnedCompanyResponse = yield call(api.auth.createUserOwnedCompany, {
        companyName: newCommunityName,
      })
      companyId = createUserOwnedCompanyResponse.companyId
      signUpCode = createUserOwnedCompanyResponse.signUpCode
    } catch (e: any) {
      const notification: INotification = {
        type: 'warning',
        content: e.message,
      }
      yield put({ type: String(addNotification), payload: notification })
      // Reset submit state if company creation fails.
      yield put({ type: String(createGoalFail) })
      return
    }
  }

  try {
    // @ts-ignore
    const response = yield call(api.activity.createGoal, {
      companyId,
      divisionId,
      endDate,
      progressType,
      startDate,
      voucher,
      dailyStepsGoal,
      activityFormulaTarget,
      collectiveGoal,
      title,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      chatEnabled,
      dailyPersistenceGoal,
      persistenceGoal,
      customType,
      customName,
      customUnit,
      customGoal,
      description,
      manualEntryEnabled,
      pointsLogic,
      totalPointsLogic,
      roundDailyPoints,
      autoJoinEnabled,
      maxManualEntryDays,
      simpleManualEntry,
      includeInTotalPointsChallenges,
      dailyMax,
      leaderboards,
      goals,
      avgAgg,
      showGroupMembers,
      showGroupMembersContributions,
      hideLeaderboardTables,
    })

    yield put({ type: String(createGoalSuccess), payload: response })

    const notification: INotification = {
      type: 'success',
      content: t`Challenge successfully created` + (signUpCode ? '. ' + t`Sign-up code` + ': ' + signUpCode : ''),
    }

    // If future start date, set "Future", otherwise "Present".
    if (convertUTCToLocalDate(new Date(startDate)) > new Date()) {
      yield put({ type: String(setActivityListTab), payload: 2 })
    } else {
      yield put({ type: String(setActivityListTab), payload: 1 })
    }
    // If we have signUpCode, set "Private", otherwise "Organization".
    const userDto: UserProfileDto = yield select(userDtoSelector)
    const managedCompanies: UserCompanyDto[] = yield select(managedCompaniesSelector)
    const ownedCompany = managedCompanies.find((c) => c.companyId === companyId)?.ownerUserId === userDto.userId
    yield put({ type: String(setActivityListTypeTab), payload: signUpCode || ownedCompany ? 0 : 1 })

    yield put(push(ROUTES.CLIENT_ACTIVITY.to, { notification }))

    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
  } catch (e: any) {
    const createGoalErrorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('challenge creation'),
    }
    yield put({ type: String(addNotification), payload: createGoalErrorNotification })
    // Reset submit state on create error.
    yield put({ type: String(createGoalFail) })
  }
}

function* sagaUpdateGoal({ payload }: any) {
  try {
    const {
      companyId,
      goalId,
      endDate,
      progressType,
      startDate,
      voucher,
      dailyStepsGoal,
      activityFormulaTarget,
      collectiveGoal,
      title,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      chatEnabled,
      dailyPersistenceGoal,
      persistenceGoal,
      customType,
      customName,
      customUnit,
      customGoal,
      description,
      manualEntryEnabled,
      pointsLogic,
      totalPointsLogic,
      roundDailyPoints,
      autoJoinEnabled,
      maxManualEntryDays,
      simpleManualEntry,
      includeInTotalPointsChallenges,
      dailyMax,
      leaderboards,
      goals,
      avgAgg,
      showGroupMembers,
      showGroupMembersContributions,
      hideLeaderboardTables,
    } = payload
    console.log('avgAgg sagaUpdateGoal', avgAgg)
    // @ts-ignore
    const response = yield call(api.activity.updateGoal, {
      companyId,
      goalId,
      endDate,
      progressType,
      startDate,
      voucher,
      dailyStepsGoal,
      activityFormulaTarget,
      collectiveGoal,
      title,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      chatEnabled,
      dailyPersistenceGoal,
      persistenceGoal,
      customType,
      customName,
      customUnit,
      customGoal,
      description,
      manualEntryEnabled,
      pointsLogic,
      totalPointsLogic,
      roundDailyPoints,
      autoJoinEnabled,
      maxManualEntryDays,
      simpleManualEntry,
      includeInTotalPointsChallenges,
      dailyMax,
      leaderboards,
      goals,
      avgAgg,
      showGroupMembers,
      showGroupMembersContributions,
      hideLeaderboardTables,
    })

    yield put({ type: String(updateGoalSuccess), payload: response })

    const notification: INotification = {
      type: 'success',
      content: t`Challenge successfully updated`,
    }

    yield put(push(ROUTES.CLIENT_ACTIVITY.to, { notification }))
  } catch (e: any) {
    const updateGoalErrorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('challenge update'),
    }
    yield put({ type: String(addNotification), payload: updateGoalErrorNotification })
    // Reset submit state on update error.
    yield put({ type: String(updateGoalFail) })
  }
}

function* sagaDeleteGoal({ payload }: Action<DeleteGoal$Request>) {
  try {
    const { goalId } = payload
    yield api.activity.deleteGoal({ goalId })

    yield put({ type: String(getCurrentCompanyGoal) })

    const notification: INotification = {
      type: 'success',
      content: t`Challenge successfully deleted`,
    }

    yield put(push(ROUTES.CLIENT_ACTIVITY.to, { notification }))
  } catch (e: any) {
    const deleteFailsNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('challenge deletion'),
    }

    yield put({ type: String(addNotification), payload: deleteFailsNotification })
  }
}

export function* benefitsManagementSagas() {
  yield all([
    takeEvery(String(createGoal), sagaCreateGoal),
    takeEvery(String(updateGoal), sagaUpdateGoal),
    takeEvery(String(deleteGoal), sagaDeleteGoal),
  ])
}
